import { callDuration, systemNoteText } from './callText';
/**
 * Last-message preview for chat lists (mobile Phase 1 fix): "Sarah: Dock 2 door is stuck", "You: Photo". Written once:
 * the API builds `text` with previewText (so mention names and entity labels are resolved where access is known), the
 * clients add the sender with previewLine. Pure TS, no DOM.
 */
import { ENTITY_TOKEN } from './messages';

export interface ChatLastMessage {
  id: string;
  sender_id: string | null;
  /** First name only. */
  sender_name: string | null;
  kind: 'user' | 'system';
  /** Plain text, already shortened. */
  text: string;
  created_at: string;
  /** t289u follow-up: a call note's outcome, so the list can word it for whoever is looking (see callPreview). */
  call?: CallPreviewInfo | null;
}

/** What the list needs from a call note's metadata (the same fields the chat's call line reads). */
export interface CallPreviewInfo {
  event: 'call.started' | 'call.ended';
  media: 'audio' | 'video';
  answered?: boolean | null;
  duration_s?: number | null;
  missed_user_ids?: string[];
  declined_user_ids?: string[];
}

/** The call fields of a system note's metadata, or null when it isn't a call note. */
export function callPreviewInfo(kind: string, metadata: Record<string, unknown> | null | undefined): CallPreviewInfo | null {
  const m = metadata ?? {};
  if (kind !== 'system' || (m.event !== 'call.started' && m.event !== 'call.ended')) return null;
  const ids = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined);
  return {
    event: m.event, media: m.media === 'video' ? 'video' : 'audio',
    ...(typeof m.answered === 'boolean' ? { answered: m.answered } : {}),
    ...(typeof m.duration_s === 'number' ? { duration_s: m.duration_s } : {}),
    ...(ids(m.missed_user_ids) ? { missed_user_ids: ids(m.missed_user_ids) } : {}),
    ...(ids(m.declined_user_ids) ? { declined_user_ids: ids(m.declined_user_ids) } : {}),
  };
}

/**
 * The chat list's line for a call, matching the call line in the chat (t289u): "Audio call · 16 secs", "Missed video
 * call", "You called · no answer", "You called · declined", "You declined an audio call", "Sarah started an audio call"
 * (still going). `callerId` is the note's sender: the person who started the call. Null when the outcome isn't known.
 */
export function callPreview(c: CallPreviewInfo, callerId: string | null, callerName: string | null, me?: string | null): string | null {
  const Kind = c.media === 'video' ? 'Video call' : 'Audio call';
  const kind = Kind.toLowerCase();
  const mine = Boolean(me && callerId === me);
  if (c.event === 'call.started') return `${mine ? 'You' : callerName ?? 'Someone'} started ${kind === 'audio call' ? 'an audio call' : 'a video call'}`;
  if (c.answered === true || c.answered == null) return c.duration_s ? `${Kind} · ${callDuration(c.duration_s)}` : c.answered ? Kind : null;
  const declined = c.declined_user_ids ?? [];
  if (mine) return `You called · ${declined.length ? 'declined' : 'no answer'}`;
  if (me && declined.includes(me)) return `You declined ${kind === 'audio call' ? 'an audio call' : 'a video call'}`;
  return `Missed ${kind}`;
}

export const PREVIEW_MAX = 90;

/** "Photo", "3 photos", "Video", "Voice note", "File", "2 files"; mixed kinds: "3 attachments". */
export function attachmentLabel(contentTypes: string[]): string | null {
  if (!contentTypes.length) return null;
  const kindOf = (t: string) => (t.startsWith('image/') ? 'photo' : t.startsWith('video/') ? 'video' : t.startsWith('audio/') ? 'voice' : 'file');
  const kinds = new Set(contentTypes.map(kindOf));
  const n = contentTypes.length;
  if (kinds.size > 1) return `${n} attachments`;
  const k = [...kinds][0];
  if (k === 'photo') return n === 1 ? 'Photo' : `${n} photos`;
  if (k === 'video') return n === 1 ? 'Video' : `${n} videos`;
  if (k === 'voice') return n === 1 ? 'Voice note' : `${n} voice notes`;
  return n === 1 ? 'File' : `${n} files`;
}

/** Message body (wire format) to one plain line: mentions as @Name, # mentions as their kind, markdown removed. */
export function plainSnippet(body: string, nameOf: (userId: string) => string | null, max = PREVIEW_MAX): string {
  let t = body
    .replace(/<@([0-9a-f-]{36})>/gi, (_m, id: string) => `@${nameOf(id.toLowerCase()) ?? 'someone'}`)
    .replace(ENTITY_TOKEN, (_m, kind: string) => `#${kind.toLowerCase()}`)
    .replace(/```[a-z]*\n?([\s\S]*?)```/gi, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(?:#{1,6}\s+|>\s?|[-*+]\s+|\d+[.)]\s+)/gm, '')
    .replace(/(\*\*|__|~~|`)/g, '')
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,!?:;]|$)/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length > max) t = `${t.slice(0, max - 1).trimEnd()}…`;
  return t;
}

/** The preview text for a message: snippet, with its attachments / poll / form named first. */
export function previewText(m: { body: string; kind: string; metadata?: Record<string, unknown> | null; attachment_types?: string[] },
  nameOf: (userId: string) => string | null, max = PREVIEW_MAX): string {
  const meta = m.metadata ?? {};
  const label = meta.poll_id ? 'Poll' : meta.form ? 'Form' : attachmentLabel(m.attachment_types ?? []);
  const snippet = plainSnippet(m.body ?? '', nameOf, max);
  // Poll and form bodies often name themselves already ("📊 Poll: …"): don't say it twice.
  if (label && snippet && new RegExp(`^\\W{0,4}${label}\\b`, 'i').test(snippet)) return snippet;
  if (label && snippet) return plainSnippet(`${label} · ${snippet}`, () => null, max);
  return label ?? snippet;
}

/** What the list shows: "You: …", "Sarah: …"; system lines read as a sentence ("Sarah started a call"). */
export function previewLine(p: ChatLastMessage | null | undefined, me?: string | null): string | null {
  if (!p || !p.text) return null;
  const who = p.sender_id && p.sender_id === me ? 'You' : p.sender_name;
  if (p.kind === 'system' && p.call) { const line = callPreview(p.call, p.sender_id, p.sender_name, me); if (line) return line; }
  if (p.kind === 'system') return systemPreview(p.text, who);
  return who ? `${who}: ${p.text}` : p.text;
}

/**
 * System lines in the list (Phase 2 chunk 0). Events with no actor read on their own ("Call ended · 1 min"); actions get
 * the person ("Sarah started an audio call", "You created task WH-3: …"); notices ("🎂 It's …", "⏰ J-12 running late")
 * stay as they are.
 */
const IMPERSONAL: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^call ended(?: \((.+)\))?$/i, m => ['Call ended', ...(m[1] ? m[1].split(' · ') : [])].join(' · ')],
  [/^(?:the )?call was missed$/i, () => 'Missed call'],
];
export function systemPreview(text: string, who: string | null): string {
  const t = systemNoteText(text);
  for (const [re, f] of IMPERSONAL) { const m = t.match(re); if (m) return f(m); }
  if (!/^[a-z]/.test(t)) return t;
  return who ? `${who} ${t}` : t.charAt(0).toUpperCase() + t.slice(1);
}

export const firstName = (displayName: string | null | undefined) => (displayName ?? '').trim().split(/\s+/)[0] || null;

/** A new message as the list preview (realtime patch on the client); null for thread replies and deleted messages. */
export function lastMessageOf(m: { id: string; sender_id: string | null; kind: string; body: string; metadata?: Record<string, unknown> | null; created_at: string;
  parent_id?: string | null; deleted_at?: string | null; attachments?: { content_type: string }[] }, nameOf: (userId: string) => string | null): ChatLastMessage | null {
  if (m.parent_id || m.deleted_at) return null;
  const text = previewText({ body: m.body, kind: m.kind, metadata: m.metadata, attachment_types: (m.attachments ?? []).map(a => a.content_type) }, nameOf);
  const call = callPreviewInfo(m.kind, m.metadata);
  return { id: m.id, sender_id: m.sender_id, sender_name: firstName(m.sender_id ? nameOf(m.sender_id) : null), kind: m.kind === 'system' ? 'system' : 'user', text, created_at: m.created_at, ...(call ? { call } : {}) };
}
